import { DeleteOutlined } from '@ant-design/icons';
import { t } from '@hefe/i18n';
import { HolidayWithGyms } from '@hefe/models';
import { GymsSlice, LocationClosuresApi } from '@hefe/store';
import { dateFormat, formatDate } from '@hefe/utils';
import {
  Button,
  Col,
  DatePicker,
  Row,
  Select,
  Space,
  Table,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

interface SyncHolidaysComponentProps {
  setHolidaySyncData: (data: HolidayWithGyms[]) => void;
}

export default function SyncHolidaysComponent(
  props: SyncHolidaysComponentProps
) {
  const gyms = useSelector(GymsSlice.gymsSelectSelector);

  const [tempData, setTempData] = useState<HolidayWithGyms[]>([]);

  const [fetchHolidays, { data, isFetching }] =
    LocationClosuresApi.useLazyFetchHolidaysQuery();

  useEffect(() => {
    setTempData(data ?? []);
  }, [data]);

  useEffect(() => {
    props.setHolidaySyncData(tempData);
  }, [tempData, props]);

  const onDateSelect = (value: dayjs.Dayjs | null) => {
    const date = value ?? dayjs();

    fetchHolidays({ until: date?.format('YYYY-MM-DD') });
  };

  const removeHoliday = (holiday: HolidayWithGyms) => {
    setTempData((prev) => prev.filter((h) => h.id !== holiday.id));
  };

  const setSelectedGyms = (value: number[], holiday: HolidayWithGyms) => {
    setTempData((prev) => {
      const index = prev.findIndex((h) => h.id === holiday.id);
      if (index === -1) return prev;

      const updatedHoliday = { ...prev[index], gym_ids: value };
      const updatedData = [...prev];
      updatedData[index] = updatedHoliday;
      return updatedData;
    });
  };

  return (
    <>
      <Row>
        <Col xs={24} style={{ marginTop: 20, marginBottom: 20 }}>
          <Typography.Paragraph>
            {t('gyms.closures.vacations.sync.description')}
          </Typography.Paragraph>
          <Typography.Paragraph>
            {t('gyms.closures.practice_sync_info')}
          </Typography.Paragraph>
          <Space>
            <DatePicker
              format={dateFormat()}
              style={{ minWidth: 200 }}
              onChange={(value) => onDateSelect(value)}
              // disabled date for more than 1 year
              disabledDate={(current) =>
                current && current > dayjs().add(1, 'year')
              }
            />
          </Space>
        </Col>
      </Row>
      <Row>
        <Col xs={24}>
          <Table
            loading={isFetching}
            columns={[
              {
                key: 'name',
                title: t('name'),
                dataIndex: 'name',
              },
              {
                key: 'from',
                title: t('from'),
                dataIndex: 'from',
                render: (value) => formatDate(value),
              },
              {
                key: 'until',
                title: t('till'),
                dataIndex: 'until',
                render: (value) => formatDate(value),
              },
              {
                key: 'gym_ids',
                title: t('gyms.plural'),
                dataIndex: 'gym_ids',
                render: (value: number[], record: HolidayWithGyms) => {
                  return (
                    <Select
                      allowClear
                      value={value}
                      options={gyms}
                      mode="multiple"
                      onChange={(value) => setSelectedGyms(value, record)}
                      style={{ minWidth: 200, maxWidth: 400 }}
                    />
                  );
                },
              },
              {
                key: 'delete',
                title: '...',
                dataIndex: '',
                render: (data, record) => {
                  return (
                    <Button
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => removeHoliday(record)}
                    />
                  );
                },
              },
            ]}
            dataSource={tempData ?? []}
          />
        </Col>
      </Row>
    </>
  );
}
